import { createContext, ReactNode, useCallback, useContext, useRef, useState } from 'react';

/*
 * Единый диалог подтверждения для всего приложения.
 *
 * Раньше каждое опасное действие спрашивало через системный confirm —
 * окна выглядели по-разному и появлялись где придётся. Теперь вопрос
 * задаётся одним компонентом в стиле приложения, по центру экрана:
 *
 *   const ask = useConfirm();
 *   if (!(await ask({ title: 'Удалить файл?', danger: true }))) return;
 *
 * Тот же компонент показывает сообщения без выбора — с одной кнопкой.
 */

export type ConfirmOptions = {
  title: string;
  text?: ReactNode;
  okText?: string;
  cancelText?: string;
  /** Красная кнопка: удаление и прочие необратимые действия. */
  danger?: boolean;
  /** Сообщение без выбора — только «Понятно». */
  notice?: boolean;
  /**
   * text содержит блочную разметку (div, textarea и т.п.) — раньше её
   * безусловно заворачивали в <p>, из-за чего блочные теги оказывались
   * внутри параграфа (невалидный DOM, React ругается на вложенность).
   * При true text рендерится как есть, без обёртки.
   */
  richText?: boolean;
};

type Ask = (options: ConfirmOptions) => Promise<boolean>;

const Ctx = createContext<Ask>(async () => true);

export const useConfirm = (): Ask => useContext(Ctx);

export function ConfirmHost({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((ok: boolean) => void) | null>(null);

  const ask = useCallback<Ask>((options) => {
    setCurrent(options);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const close = (ok: boolean) => {
    setCurrent(null);
    resolver.current?.(ok);
    resolver.current = null;
  };

  return (
    <Ctx.Provider value={ask}>
      {children}
      {current && (
        <div className="backdrop confirm" onClick={(e) => e.target === e.currentTarget && close(false)}>
          <div className="dialog" role="dialog" aria-modal="true">
            <div className="dialog-body">
              <h2>{current.title}</h2>
              {current.text ? current.richText ? current.text : <p>{current.text}</p> : null}
            </div>
            <div className="dialog-foot">
              {!current.notice && (
                <button className="btn outline" onClick={() => close(false)}>
                  {current.cancelText ?? 'Отмена'}
                </button>
              )}
              <button
                className={`btn ${current.danger ? 'danger' : 'primary'}`}
                autoFocus
                onClick={() => close(true)}
              >
                {current.okText ?? (current.notice ? 'Понятно' : 'Подтвердить')}
              </button>
            </div>
          </div>
        </div>
      )}
    </Ctx.Provider>
  );
}
